<script>
  import { workspace, refreshWorkspace } from '../lib/stores/workspace.js';
  import { auth } from '../lib/stores/auth.svelte.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';

  let open = $state(false);
  // A workspace belongs to a company (shared with its HQ team, like the marketing board) or, with no company, to
  // one person. fn_bb_my_workspaces lists yours, then your companies that don't have one yet.
  let allVentures = $state([]);
  let companiesWithout = $state([]);
  let quota = $state(null);
  let loading = $state(false);
  let creating = $state(false);
  let newVentureName = $state('');
  let showCreateForm = $state(false);

  let editingVentureId = $state(null);
  let editingVentureName = $state('');
  let savingRename = $state(false);

  function startRename(v, e) {
    e.stopPropagation();
    editingVentureId = v.id;
    editingVentureName = v.name;
  }
  function cancelRename() {
    editingVentureId = null;
    editingVentureName = '';
  }
  async function saveRename(ventureId) {
    const trimmed = editingVentureName.trim();
    if (!trimmed) { showToast('Name cannot be empty', 'error'); return; }
    savingRename = true;
    try {
      const { error } = await supabase.from('bb_workspaces')
        .update({ name: trimmed })
        .eq('id', ventureId);
      if (error) throw error;
      await loadVentures();
      if (ventureId === workspace.id) {
        await refreshWorkspace(ventureId);
      }
      cancelRename();
      showToast('Renamed', 'success');
    } catch (e) {
      showToast('Rename failed: ' + e.message, 'error');
    } finally {
      savingRename = false;
    }
  }

  async function loadVentures() {
    if (!auth.profile?.id) return;
    const { data: rows } = await supabase.rpc('fn_bb_my_workspaces');
    const list = rows || [];
    allVentures = list.filter(r => r.has_workspace).map(r => ({ id: r.workspace_id, name: r.name, partner_id: r.partner_id }))
      .sort((a, b) => (a.partner_id ? 0 : 1) - (b.partner_id ? 0 : 1));
    companiesWithout = list.filter(r => !r.has_workspace);
    const { data: q } = await supabase.from('bb_user_quota')
      .select('*')
      .eq('owner_profile_id', auth.profile.id)
      .maybeSingle();
    quota = q;
  }

  $effect(() => {
    if (open) loadVentures();
  });

  $effect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!e.target.closest('.venture-switcher')) {
        open = false;
        showCreateForm = false;
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  });

  function toggleOpen(e) {
    e.stopPropagation();
    open = !open;
    if (!open) showCreateForm = false;
  }

  async function switchVenture(v) {
    if (v.id === workspace.id) { open = false; return; }
    loading = true;
    try {
      localStorage.setItem('bb_active_workspace_id', v.id);
      await refreshWorkspace(v.id);
      open = false;
      showToast(`Switched to ${v.name}`, 'success');
    } catch (e) {
      showToast('Switch failed: ' + e.message, 'error');
    } finally {
      loading = false;
    }
  }

  // Personal workspaces count against your plan; a company's workspace doesn't.
  const canCreate = $derived.by(() => {
    if (!quota) return false;
    if (quota.tier === 'pro') return true;
    return allVentures.filter(v => !v.partner_id).length < (quota.workspace_slots || 1);
  });

  async function startCompany(c) {
    creating = true;
    try {
      const { data, error } = await supabase.from('bb_workspaces').insert({
        owner_profile_id: auth.profile.id, partner_id: c.partner_id, name: c.name
      }).select().single();
      if (error) throw error;
      localStorage.setItem('bb_active_workspace_id', data.id);
      await refreshWorkspace(data.id);
      open = false;
      showToast(`${data.name} started`, 'success');
    } catch (e) {
      showToast('Could not start it: ' + e.message, 'error');
    } finally {
      creating = false;
    }
  }

  const showUpgradeOption = $derived.by(() => {
    if (!quota) return false;
    return !canCreate;
  });

  async function createVenture() {
    if (!newVentureName.trim()) return;
    creating = true;
    try {
      const { data: gate } = await supabase.rpc('bb_can_create_workspace', { p_profile_id: auth.profile.id });
      if (gate && gate.allowed === false) {
        showToast(`At your limit (${gate?.used}/${gate?.limit}). Upgrade to add more.`, 'error');
        creating = false;
        return;
      }
      const { data, error } = await supabase.from('bb_workspaces').insert({
        owner_profile_id: auth.profile.id,
        name: newVentureName.trim()
      }).select().single();
      if (error) throw error;
      newVentureName = '';
      showCreateForm = false;
      await loadVentures();
      localStorage.setItem('bb_active_workspace_id', data.id);
      await refreshWorkspace(data.id);
      open = false;
      showToast(`"${data.name}" created`, 'success');
    } catch (e) {
      showToast('Create failed: ' + e.message, 'error');
    } finally {
      creating = false;
    }
  }

  function goToPricing() {
    open = false;
    window.dispatchEvent(new CustomEvent('navigate', { detail: { route: 'pricing' } }));
  }
</script>

<div class="venture-switcher" class:open>
  <button class="venture-trigger" onclick={toggleOpen} type="button">
    <span class="venture-label">Design in</span>
    <span class="venture-name">{workspace.name || 'Untitled'}</span>
    <span class="venture-chev">▾</span>
  </button>

  {#if open}
    <div class="venture-panel" role="menu" onclick={(e) => e.stopPropagation()}>
      <div class="venture-panel-header">Design in</div>
      <div class="venture-list">
        {#each allVentures as v (v.id)}
          {#if editingVentureId === v.id}
            <div class="venture-item venture-item-editing">
              <input
                class="venture-rename-input"
                bind:value={editingVentureName}
                onkeydown={(e) => { if (e.key === 'Enter') saveRename(v.id); if (e.key === 'Escape') cancelRename(); }}
                autofocus
              />
              <button class="vr-save" onclick={() => saveRename(v.id)} disabled={savingRename || !editingVentureName.trim()} type="button">
                {savingRename ? '…' : 'Save'}
              </button>
              <button class="vr-cancel" onclick={cancelRename} type="button">Cancel</button>
            </div>
          {:else}
            <div class="venture-item-wrap" class:active={v.id === workspace.id}>
              <button
                class="venture-item"
                onclick={() => switchVenture(v)}
                disabled={loading}
                type="button"
              >
                <span class="venture-item-name">{v.name}</span>
                {#if v.id === workspace.id}<span class="venture-item-active">active</span>{/if}
              </button>
              <button class="venture-item-rename" onclick={(e) => startRename(v, e)} title="Rename" type="button">✎</button>
            </div>
          {/if}
        {/each}
      </div>

      {#if companiesWithout.length}
        <div class="venture-panel-header">Start for</div>
        <div class="venture-list">
          {#each companiesWithout as c (c.partner_id)}
            <button class="venture-item venture-start" onclick={() => startCompany(c)} disabled={creating} type="button">
              <span class="venture-item-name">+ {c.name}</span>
            </button>
          {/each}
        </div>
      {/if}

      <div class="venture-panel-footer">
        {#if showCreateForm}
          <div class="venture-create-row">
            <input
              bind:value={newVentureName}
              placeholder="Name"
              onkeydown={(e) => { if (e.key === 'Enter') createVenture(); if (e.key === 'Escape') showCreateForm = false; }}
            />
            <button class="vc-confirm" onclick={createVenture} disabled={creating || !newVentureName.trim()} type="button">
              {creating ? 'Creating…' : 'Create'}
            </button>
            <button class="vc-cancel" onclick={() => { showCreateForm = false; newVentureName = ''; }} type="button">Cancel</button>
          </div>
        {:else if canCreate}
          <button class="venture-add" onclick={() => showCreateForm = true} type="button">+ New personal design</button>
        {:else if showUpgradeOption}
          <button class="venture-upgrade" onclick={goToPricing} type="button">
            Upgrade to add more →
          </button>
          <div class="venture-quota-hint">
            {allVentures.filter(v => !v.partner_id).length}/{quota?.workspace_slots ?? 1} personal designs used (tier: {quota?.tier || 'free'})
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .venture-switcher { position: relative; }
  .venture-trigger {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 6px 14px; border-radius: 999px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    color: #f0efe9; cursor: pointer; transition: background 0.15s ease, border-color 0.15s ease;
    font-family: inherit; margin: 0;
  }
  .venture-trigger:hover { background: rgba(212,175,55,0.1); border-color: rgba(212,175,55,0.3); }
  .venture-label { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 1.4px; text-transform: uppercase; color: #9aa0a6; }
  .venture-name { font-size: 13px; font-weight: 500; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .venture-chev { color: #9aa0a6; font-size: 10px; }

  .venture-panel {
    position: absolute; top: calc(100% + 8px); left: 0;
    min-width: 280px; max-width: 360px;
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.2); border-radius: 14px;
    box-shadow: 0 16px 48px rgba(0,0,0,0.6);
    z-index: 100;
    overflow: hidden;
  }
  .venture-panel-header {
    padding: 10px 14px;
    font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 1.4px;
    text-transform: uppercase; color: #9aa0a6;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .venture-list { display: flex; flex-direction: column; max-height: 280px; overflow-y: auto; }
  .venture-item {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px;
    background: none; border: none; color: #d8d8dc; cursor: pointer;
    text-align: left; font-size: 13px;
    transition: background 0.12s ease;
    margin: 0;
  }
  .venture-item:hover:not(:disabled) { background: rgba(255,255,255,0.03); }
  .venture-item:disabled { opacity: 0.4; cursor: not-allowed; }
  .venture-item.active { background: rgba(212,175,55,0.08); color: #f0efe9; }
  .venture-item-active {
    font-family: 'JetBrains Mono', monospace; font-size: 9px;
    letter-spacing: 1px; text-transform: uppercase;
    color: #d4af37;
    background: rgba(212,175,55,0.12);
    padding: 2px 6px; border-radius: 4px;
  }
  .venture-start { color: #9aa0a6; }
  .venture-item-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .venture-item-wrap {
    position: relative;
    display: flex;
    align-items: center;
    transition: background 0.12s ease;
  }
  .venture-item-wrap.active { background: rgba(212,175,55,0.08); }
  .venture-item-wrap:hover:not(.active) { background: rgba(255,255,255,0.03); }
  .venture-item-wrap .venture-item { flex: 1; background: none; }
  .venture-item-wrap .venture-item:hover:not(:disabled) { background: none; }
  .venture-item-rename {
    background: none; border: none; color: #6a6a6e;
    cursor: pointer; padding: 6px 10px; font-size: 13px;
    opacity: 0; transition: opacity 0.15s ease, color 0.15s ease;
    margin: 0;
  }
  .venture-item-wrap:hover .venture-item-rename { opacity: 1; }
  .venture-item-rename:hover { color: #d4af37; }

  .venture-item-editing {
    display: flex; gap: 6px; align-items: center;
    padding: 8px 10px;
    background: rgba(212,175,55,0.06);
  }
  .venture-rename-input {
    flex: 1;
    background: rgba(0,0,0,0.4); border: 1px solid rgba(212,175,55,0.3);
    color: #f4f4f6; padding: 6px 10px; border-radius: 6px; font-size: 13px;
    margin: 0; font-family: inherit;
  }
  .venture-rename-input:focus { outline: none; border-color: rgba(212,175,55,0.6); }
  .vr-save {
    background: linear-gradient(180deg, #d4af37, #b8941f); color: #0a0a0c;
    border: none; padding: 6px 10px; border-radius: 6px;
    cursor: pointer; font-weight: 600; font-size: 11px; margin: 0;
  }
  .vr-save:disabled { opacity: 0.4; cursor: not-allowed; }
  .vr-cancel {
    background: rgba(255,255,255,0.05); color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.1); padding: 6px 10px; border-radius: 6px;
    cursor: pointer; font-size: 11px; margin: 0;
  }

  .venture-panel-footer {
    padding: 10px 14px;
    border-top: 1px solid rgba(255,255,255,0.04);
    background: rgba(255,255,255,0.01);
  }
  .venture-add {
    width: 100%; padding: 8px 12px;
    background: rgba(212,175,55,0.08); color: #d4af37;
    border: 1px dashed rgba(212,175,55,0.3); border-radius: 8px;
    cursor: pointer; font-size: 12px; margin: 0;
  }
  .venture-add:hover { background: rgba(212,175,55,0.15); border-style: solid; }

  .venture-create-row { display: flex; gap: 6px; align-items: center; }
  .venture-create-row input {
    flex: 1; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6; padding: 7px 10px; border-radius: 6px; font-size: 12px;
    margin: 0; font-family: inherit;
  }
  .vc-confirm {
    background: linear-gradient(180deg, #d4af37, #b8941f); color: #0a0a0c;
    border: none; padding: 7px 12px; border-radius: 6px;
    cursor: pointer; font-weight: 600; font-size: 11px; margin: 0;
  }
  .vc-confirm:disabled { opacity: 0.4; cursor: not-allowed; }
  .vc-cancel {
    background: rgba(255,255,255,0.05); color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.1); padding: 7px 12px; border-radius: 6px;
    cursor: pointer; font-size: 11px; margin: 0;
  }

  .venture-upgrade {
    width: 100%; padding: 8px 12px;
    background: linear-gradient(180deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04));
    color: #d4af37; border: 1px solid rgba(212,175,55,0.3); border-radius: 8px;
    cursor: pointer; font-size: 12px; font-weight: 500; margin: 0;
  }
  .venture-upgrade:hover { background: rgba(212,175,55,0.2); }
  .venture-quota-hint {
    margin-top: 6px; font-size: 10px; color: #9aa0a6;
    text-align: center; font-family: 'JetBrains Mono', monospace;
  }
</style>
